import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {addToast} from 'common/components/toast-area/toast-manager';

import {LITERAL_NAME_REGEX, MAX_CONDITIONS} from '../models/constants';
import {getLabelParts, isCohortLiteral, isEventLiteral} from '../util/staged-cohort';
import {TrackingEvent} from '../util/tracking';
import template from './index.jade';
import css from './index.styl?inline';
import './literal-clause/cohort-clause';
import './literal-clause/event-clause';
import './literal-clause/people-clause';

export class CohortsLiteralBuilder extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      disabled: {type: `boolean`, description: `whether this builder can be edited`},
      'user-id': {type: `number`, description: `ID of user viewing the builder`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  shouldUpdate(state) {
    return !!state.stagedCohort && !!state.stagedCohort.literals[this.literalIdx];
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      helpers: {
        addClauseToLiteral: () => {
          const {stagedCohort} = this.state;
          this.app.track(TrackingEvent.AddClause, stagedCohort.toTrackingData());
          this.app.updateStagedCohort(stagedCohort.addClauseToLiteralAtIndex(this.literalIdx));
        },

        removeLiteral: () => {
          const {stagedCohort: initialStagedCohort} = this.state;

          this.app.track(
            TrackingEvent.RemoveLiteral,
            Object.assign(
              {},
              initialStagedCohort.toTrackingData(),
              initialStagedCohort.getTrackingDataForLiteral(this.literalIdx),
            ),
          );
          const updatedStagedCohort = initialStagedCohort.removeLiteral(this.literalIdx);

          // references to this.app and this.literal are undefined when accessed through undoCallback
          const {app, literal} = this;

          const undoCallback = () => {
            const {stagedCohort} = this.state;
            app.track(
              TrackingEvent.RestoreLiteral,
              Object.assign({}, updatedStagedCohort.toTrackingData(), literal.toTrackingData()),
            );
            let stagedCohortWithLiteral = initialStagedCohort;
            // if there are any changes, add the literal to the end of the staged cohort
            if (stagedCohort !== updatedStagedCohort) {
              stagedCohortWithLiteral = stagedCohort.restoreLiteral(literal);
            }
            app.updateStagedCohort(stagedCohortWithLiteral);
          };

          const toast = {
            undoable: true,
            hide: false,
            message: `Removed group ${this.literal.labelPrefix}`,
            undoCallback,
            timeout: 7000,
          };

          this.app.updateStagedCohort(updatedStagedCohort);
          this.update({builderScreen: this.app.defaultBuilderScreenState});
          addToast(toast);
        },
        getLiteralIndex: () => this.literalIdx,
        getLiteralLabelParts: () => {
          let parts;
          if (this.state.stagedCohort.numLiterals === 1 && this.literal.clauses.length === 1) {
            parts = [{text: `Group ${this.literal.labelPrefix}`}];
          } else {
            parts = getLabelParts(this.literal.getLabel(), LITERAL_NAME_REGEX);
          }
          return parts;
        },
        onGripperMouseDown: () => {
          window.getSelection().removeAllRanges();
          if (this.state.builderScreen.isOpen) {
            this.app.closeBuilder(this.state.builderScreen.builderType, this.state.stagedClausePosition);
          }
        },
        onGripperMouseEnter: () => this.update({cursorOnGripper: true}),
        onGripperMouseLeave: () => this.update({cursorOnGripper: false}),
        getLiteralComponentName: () => {
          if (isEventLiteral(this.literal)) {
            return `cohorts-event-clause`;
          } else if (isCohortLiteral(this.literal)) {
            return `cohorts-cohort-clause`;
          } else {
            return `cohorts-people-clause`;
          }
        },
        getLiteralComponentAttrs: (clauseIdx, literalIdx, isLastClause) => {
          const attrs = {
            'data-clause-idx': clauseIdx,
            'data-literal-idx': literalIdx,
            'is-last-clause': isLastClause,
            'user-id': this.attr(`user-id`),
          };
          if (isEventLiteral(this.literal)) {
            attrs[`project-as-of-date`] = this.attr(`project-as-of-date`);
          }

          return attrs;
        },
        getAddConditionDisabledMessage: () => {
          if (isCohortLiteral(this.literal)) {
            return `Cannot add conditions to Cohort block`;
          } else if (this.literal.clauses.length >= MAX_CONDITIONS) {
            return `Cannot add more than ${MAX_CONDITIONS} conditions to a block`;
          }
        },
      },
    };
  }

  get literal() {
    return this.state.stagedCohort.literals[this.literalIdx];
  }

  get literalIdx() {
    return Number(this.dataset.literalIdx);
  }
}

export const CohortsLiteralBuilderEl = defineAndMakeJsxFactory(`cohorts-literal-builder`, CohortsLiteralBuilder);
